import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, HelpCircle, Lightbulb, ListChecks, MessageCircle, Play, RefreshCw, Send, Sparkles, TrendingUp, XCircle } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { useTour } from "../components/TourContext";
import { api } from "../lib/api";

// Very small markdown → JSX renderer (headings, bullet lists, bold, quote).
function renderSection(text, idx) {
  const lines = String(text || "").split("\n");
  const blocks = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length) {
      blocks.push(
        <ul key={`ul-${idx}-${blocks.length}`}>
          {bulletBuffer.map((line, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(line.replace(/^\s*-\s+/, "")) }} />
          ))}
        </ul>
      );
      bulletBuffer = [];
    }
  };

  lines.forEach((line, i) => {
    const l = line.trim();
    if (l.startsWith("## ")) {
      flushBullets();
      blocks.push(<h3 key={`h-${idx}-${i}`}>{l.slice(3)}</h3>);
    } else if (l.startsWith("- ")) {
      bulletBuffer.push(l);
    } else if (l.startsWith("> ")) {
      flushBullets();
      blocks.push(<blockquote key={`q-${idx}-${i}`} dangerouslySetInnerHTML={{ __html: inline(l.slice(2)) }} />);
    } else if (l) {
      flushBullets();
      blocks.push(<p key={`p-${idx}-${i}`} dangerouslySetInnerHTML={{ __html: inline(l) }} />);
    }
  });
  flushBullets();
  return blocks;
}

function inline(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}

export default function AcademiaLessonPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { start: startTour } = useTour();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [question, setQuestion] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatLog, setChatLog] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const chatEndRef = useRef(null);

  const load = () =>
    api.get(`/academia/lessons/${slug}`, { withCredentials: true })
      .then((r) => setLesson(r.data))
      .catch((e) => setError(e.response?.data?.detail || "No pudimos cargar la lección."));

  useEffect(() => { load(); setChatLog([]); setQuestion(""); setQuizAnswers({}); }, [slug]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatLog]);

  const markStatus = async (status) => {
    setSaving(true);
    try {
      await api.post(`/academia/lessons/${slug}/progress`, { status }, { withCredentials: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos actualizar el progreso.");
    } finally {
      setSaving(false);
    }
  };

  const askConta = async (event) => {
    event?.preventDefault();
    const q = question.trim();
    if (!q || chatBusy) return;
    setChatBusy(true);
    setChatLog((prev) => [...prev, { role: "user", text: q }]);
    setQuestion("");
    try {
      const response = await api.post("/academia/chat", { question: q, lesson_slug: slug }, { withCredentials: true });
      setChatLog((prev) => [...prev, { role: "conta", text: response.data.answer }]);
    } catch (e) {
      setChatLog((prev) => [...prev, { role: "conta", text: e.response?.data?.detail || "Uy, no pude contestar. Intenta de nuevo en un momento." }]);
    } finally {
      setChatBusy(false);
    }
  };

  if (error && !lesson) {
    return (
      <AppShell>
        <main className="page academia-lesson-page">
          <p className="expenses-error" data-testid="lesson-error">{error}</p>
          <Link to="/academia" className="expenses-scan-button"><ArrowLeft size={15} /> Volver a Academia</Link>
        </main>
      </AppShell>
    );
  }

  if (!lesson) {
    return <AppShell><main className="page academia-lesson-page"><p>Cargando lección...</p></main></AppShell>;
  }

  const isDone = lesson.status === "completed";

  return (
    <AppShell>
      <main className="page academia-lesson-page" data-testid="academia-lesson-page">
        <Link to="/academia" className="academia-back" data-testid="lesson-back"><ArrowLeft size={16} /> Volver a Academia</Link>

        <header className="academia-lesson-head" style={{ borderTop: `6px solid ${lesson.category_color}` }}>
          <small style={{ color: lesson.category_color }}>{lesson.category_name}</small>
          <h1>{lesson.emoji} {lesson.title}</h1>
          <p>{lesson.tagline}</p>
          <div className="academia-lesson-meta">
            <span>⏱️ {lesson.minutes} min</span>
            {lesson.difficulty && <span data-testid="lesson-difficulty">📊 {lesson.difficulty}</span>}
            {isDone && <span className="academia-badge-done" data-testid="lesson-badge-done"><CheckCircle2 size={13} /> Completada</span>}
          </div>
        </header>

        {lesson.why_matters && (
          <article className="academia-why" data-testid="lesson-why">
            <TrendingUp size={18} />
            <div>
              <small>¿Por qué importa?</small>
              <p>{lesson.why_matters}</p>
            </div>
          </article>
        )}

        {lesson.wizard_slug && (
          <button
            type="button"
            onClick={() => startTour(lesson.wizard_slug)}
            className="academia-wizard-cta"
            data-testid={`lesson-wizard-cta-${lesson.wizard_slug}`}
          >
            <div>
              <small>Tutorial interactivo</small>
              <b>▶ Practicar en la app real</b>
              <em>Te acompaño paso a paso mientras usas el módulo de verdad.</em>
            </div>
            <Play size={22} />
          </button>
        )}

        <article className="academia-lesson-content" data-testid="lesson-content">
          {lesson.sections.map((section, i) => (
            <div key={i} className="academia-section">
              {renderSection(section, i)}
            </div>
          ))}
        </article>

        {lesson.steps?.length > 0 && (
          <article className="academia-steps" data-testid="lesson-steps">
            <h3><ListChecks size={18} /> Paso a paso</h3>
            <ol>
              {lesson.steps.map((step, i) => (
                <li key={i} data-testid={`lesson-step-${step.number || i + 1}`}>
                  <span className="step-num" style={{ background: lesson.category_color }}>{step.number || i + 1}</span>
                  <div>
                    <b>{step.title}</b>
                    <p>{step.body}</p>
                    {step.tip && <em><Lightbulb size={12} /> Tip: {step.tip}</em>}
                  </div>
                </li>
              ))}
            </ol>
          </article>
        )}

        {lesson.mistakes?.length > 0 && (
          <article className="academia-mistakes" data-testid="lesson-mistakes">
            <h3><AlertTriangle size={18} /> Errores comunes</h3>
            <ul>
              {lesson.mistakes.map((line, i) => <li key={i}><XCircle size={14} /> {line}</li>)}
            </ul>
          </article>
        )}

        {lesson.tips?.length > 0 && (
          <article className="academia-tips" data-testid="lesson-tips">
            <h3><Lightbulb size={18} /> Tips útiles</h3>
            <ul>
              {lesson.tips.map((line, i) => <li key={i}>💡 {line}</li>)}
            </ul>
          </article>
        )}

        {lesson.quiz?.length > 0 && (
          <article className="academia-quiz" data-testid="lesson-quiz">
            <h3><HelpCircle size={18} /> Mini quiz</h3>
            {lesson.quiz.map((q, qi) => {
              const chosen = quizAnswers[qi];
              return (
                <div key={qi} className="quiz-block" data-testid={`quiz-block-${qi}`}>
                  <p><b>{qi + 1}. {q.q}</b></p>
                  <div className="quiz-options">
                    {q.options.map((opt, oi) => {
                      const isChosen = chosen === oi;
                      const isCorrect = q.correct === oi;
                      let cls = "quiz-opt";
                      if (chosen !== undefined) {
                        if (isCorrect) cls += " correct";
                        else if (isChosen) cls += " wrong";
                      }
                      return (
                        <button
                          key={oi}
                          type="button"
                          className={cls}
                          disabled={chosen !== undefined}
                          onClick={() => setQuizAnswers({ ...quizAnswers, [qi]: oi })}
                          data-testid={`quiz-${qi}-opt-${oi}`}
                        >
                          {opt}
                          {chosen !== undefined && isCorrect && <CheckCircle2 size={14} />}
                          {chosen !== undefined && isChosen && !isCorrect && <XCircle size={14} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </article>
        )}

        {lesson.summary?.length > 0 && (
          <article className="academia-summary" data-testid="lesson-summary">
            <h3>✨ Resumen rápido</h3>
            <ul>
              {lesson.summary.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          </article>
        )}

        {lesson.checklist?.length > 0 && (
          <article className="academia-checklist" data-testid="lesson-checklist">
            <h3>📝 Checklist de acción</h3>
            <ul>
              {lesson.checklist.map((line, i) => (
                <li key={i}><CheckCircle2 size={16} style={{ color: "#238a65" }} /> {line}</li>
              ))}
            </ul>
          </article>
        )}

        <div className="academia-lesson-actions">
          {!isDone ? (
            <button className="expenses-add-button" onClick={() => markStatus("completed")} disabled={saving} data-testid="lesson-mark-completed">
              <CheckCircle2 size={17} /> Marcar como completada
            </button>
          ) : (
            <button className="expenses-scan-button" onClick={() => markStatus("in_progress")} disabled={saving} data-testid="lesson-reset">
              <RefreshCw size={15} /> Marcar como en progreso
            </button>
          )}
          {lesson.next_slug && (
            <button className="expenses-scan-button" onClick={() => navigate(`/academia/${lesson.next_slug}`)} data-testid="lesson-next">
              Siguiente lección <ArrowRight size={15} />
            </button>
          )}
        </div>

        {lesson.related?.length > 0 && (
          <article className="academia-related" data-testid="lesson-related">
            <h3>Lecciones relacionadas</h3>
            <div className="academia-related-row">
              {lesson.related.map((r) => (
                <Link key={r.slug} to={`/academia/${r.slug}`} className="academia-related-chip" data-testid={`lesson-related-${r.slug}`}>
                  <span>{r.emoji}</span>
                  <div>
                    <b>{r.title}</b>
                    <small>{r.tagline}</small>
                  </div>
                </Link>
              ))}
            </div>
          </article>
        )}

        <section className="academia-chat" data-testid="lesson-chat">
          <header>
            <MessageCircle size={18} />
            <div>
              <b>Pregúntale a El Conta</b>
              <small>Explica esta lección con tus dudas — en un lenguaje súper simple.</small>
            </div>
          </header>
          <div className="academia-chat-log" data-testid="lesson-chat-log">
            {chatLog.length === 0 && (
              <p className="academia-chat-empty">
                <Sparkles size={14} /> Ejemplo: <em>"No entendí bien qué es el IVA"</em>, <em>"¿Puedo deducir la gasolina?"</em>
              </p>
            )}
            {chatLog.map((msg, i) => (
              <div key={i} className={`academia-chat-bubble ${msg.role}`} data-testid={`lesson-chat-msg-${i}`}>
                <span>{msg.role === "user" ? "Tú" : "El Conta"}</span>
                <p>{msg.text}</p>
              </div>
            ))}
            {chatBusy && (
              <div className="academia-chat-bubble conta" data-testid="lesson-chat-typing">
                <span>El Conta</span>
                <p><em>Pensando...</em></p>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={askConta} className="academia-chat-form">
            <input
              type="text"
              placeholder="Escribe tu duda..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={800}
              data-testid="lesson-chat-input"
              disabled={chatBusy}
            />
            <button type="submit" disabled={!question.trim() || chatBusy} data-testid="lesson-chat-send">
              <Send size={15} />
            </button>
          </form>
        </section>
      </main>
    </AppShell>
  );
}
